/*
  <product-reviews>: shows the Judge.me reviews of a product in the markup of the design
  (sections/product-reviews.liquid). The section itself renders the average and the count from the
  standard review metafields; this element adds the rating distribution, the customer media and the
  review list.

  Judge.me data is read from the first source that gives a usable result:
    1. script[data-reviews-data]            the product metafield judgeme.review_widget_data (JSON),
                                            printed by the section;
    2. window.jdgm.data.reviewWidget[id]    the same JSON, put on the page by the Judge.me app block
                                            (also read from the text of script.jdgm-review-widget-data);
    3. .jdgm-rev-widg                       the pre-rendered Judge.me widget (metafield
                                            judgeme.widget), inside this section first, then anywhere
                                            on the page. A MutationObserver waits a few seconds for
                                            a widget that arrives late;
    4. GET <cdn.judge.me>/reviews/reviews_for_widget
                                            the endpoint the Judge.me widget calls for its pages.
                                            It answers with the JSON of source 1 or, for shops on
                                            the older widget, with { html } in the markup of source 3.
                                            "Show more" always uses this endpoint.
  When Judge.me is on the page but none of the sources can be read, the original Judge.me widget
  inside this section ([data-reviews-widget], normally hidden) is shown in place of the list.
  The list and the original widget are never visible together.

  Review texts come from a third party: they are never inserted as HTML, only as text nodes.
*/
(() => {
  if (customElements.get('product-reviews')) return;

  const API_HOST = 'https://cdn.judge.me/';
  const API_PATH = 'reviews/reviews_for_widget';
  const WAIT_FOR_WIDGET = 4000;
  const REQUEST_TIMEOUT = 8000;
  const TILE_LIMIT = 4;
  const WIDGET_ROOTS = '.jdgm-review-widget, .jdgm-rev-widg, .jm-review-widget';

  /* Video hosts used by Judge.me (taken from its widget code). */
  const BUNNY_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const BUNNY_LIBRARY = '748386';

  /* ---------- Helpers ---------- */
  const clean = (value) => (value == null ? '' : String(value)).replace(/\s+/g, ' ').trim();

  const toNumber = (value) => {
    const number = typeof value === 'number' ? value : parseFloat(String(value == null ? '' : value).replace(',', '.'));
    return Number.isFinite(number) ? number : null;
  };

  const httpsUrl = (value) => {
    const url = clean(value);
    if (url.startsWith('//')) return `https:${url}`;
    return /^https:\/\//i.test(url) ? url : '';
  };

  /* "2026-01-12 18:09:36 UTC", "2026-01-12T18:09:36.392Z" → Date */
  const toDate = (value) => {
    const raw = clean(value);
    if (!raw) return null;
    const match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (match) {
      const [, y, m, d, h = '12', min = '0', s = '0'] = match;
      const date = new Date(Date.UTC(+y, +m - 1, +d, +h, +min, +s));
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  /* Turns review HTML (or an element) into plain paragraphs. Line breaks stay as "\n". */
  const toParagraphs = (input) => {
    if (!input) return [];
    let root = input;
    if (typeof input === 'string') {
      root = new DOMParser().parseFromString(`<body>${input}</body>`, 'text/html').body;
    }
    const paragraphs = [];
    let current = '';
    const flush = () => {
      const line = current
        .replace(/[ \t\r\f\v]+/g, ' ')
        .replace(/ ?\n ?/g, '\n')
        .trim();
      if (line) paragraphs.push(line);
      current = '';
    };
    const walk = (node) => {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          current += child.textContent.replace(/\n/g, ' ');
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const tag = child.tagName;
          if (/^(SCRIPT|STYLE|IFRAME|OBJECT|EMBED|TEMPLATE|NOSCRIPT)$/.test(tag)) return;
          if (child.classList.contains('jdgm-rev__body-read-more')) return;
          if (tag === 'BR') current += '\n';
          else if (/^(P|DIV|LI|UL|OL|BLOCKQUOTE|H[1-6])$/.test(tag)) {
            flush();
            walk(child);
            flush();
          } else walk(child);
        }
      });
    };
    walk(root);
    flush();
    return paragraphs;
  };

  const initialsOf = (name, fallback, locale) => {
    const words = clean(name)
      .split(' ')
      .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
      .filter(Boolean);
    let letters = words.length ? Array.from(words[0])[0] : '';
    if (words.length > 1) letters += Array.from(words[words.length - 1])[0];
    if (!letters) letters = clean(fallback).slice(0, 2);
    try {
      return letters.toLocaleUpperCase(locale);
    } catch (error) {
      return letters.toUpperCase();
    }
  };

  /* ---------- Media ---------- */
  const videoOf = (info) => {
    let vendor = 'cloudflare';
    let id = '';
    if (typeof info === 'string') {
      id = info;
      if (BUNNY_ID.test(id)) vendor = 'bunny';
      else if (id.length <= 16) vendor = 'vimeo';
    } else if (info && typeof info === 'object') {
      if (info.media_platform_name === 'yt') {
        vendor = 'youtube';
        id = info.media_platform_url || info.id;
      } else {
        id = info.external_id || info.id;
        if (info.hosting_vendor === 'vimeo' || info.hosting_vendor === 'bunny') vendor = info.hosting_vendor;
        else if (BUNNY_ID.test(String(id || ''))) vendor = 'bunny';
      }
    }
    id = clean(id);
    if (!id || !/^[\w-]+$/.test(id)) return null;
    const key = encodeURIComponent(id);
    const library = (window.jdgm && /^\d+$/.test(String(window.jdgm.BUNNY_STREAM_LIBRARY_ID)) && window.jdgm.BUNNY_STREAM_LIBRARY_ID) || BUNNY_LIBRARY;
    const hosts = {
      youtube: [`https://www.youtube-nocookie.com/embed/${key}?playsinline=1&rel=0`, `https://img.youtube.com/vi/${key}/0.jpg`],
      vimeo: [`https://player.vimeo.com/video/${key}?playsinline=1`, `https://i.vimeocdn.com/video/${key}_200.jpg`],
      bunny: [`https://iframe.mediadelivery.net/embed/${library}/${key}?preload=false&responsive=true`, `https://review-videos.judgeme.com/${key}/thumbnail.jpg`],
      cloudflare: [`https://iframe.videodelivery.net/${key}?controls=true&playsinline=1`, `https://videodelivery.net/${key}/thumbnails/thumbnail.jpg?time=1s&height=320`],
    };
    return { type: 'video', src: hosts[vendor][0], thumb: hosts[vendor][1] };
  };

  /* Pictures and videos of one review in the Judge.me JSON shape. */
  const mediaOf = (review) => {
    const items = [];
    const seen = new Set();
    const add = (item) => {
      if (!item || !item.src || seen.has(item.src)) return;
      seen.add(item.src);
      items.push(item);
    };
    (Array.isArray(review.pictures_urls) ? review.pictures_urls : []).forEach((picture) => {
      if (!picture) return;
      const src = httpsUrl(picture.huge || picture.original || picture.compact || picture.small);
      const thumb = httpsUrl(picture.compact || picture.small || picture.huge || picture.original);
      if (src) add({ type: 'image', src, thumb: thumb || src });
    });
    (Array.isArray(review.video_external_ids) ? review.video_external_ids : []).forEach((id) => add(videoOf(String(id))));
    (Array.isArray(review.media_platform_hosted_video_infos) ? review.media_platform_hosted_video_infos : []).forEach((info) => add(videoOf(info)));
    return items;
  };

  /* ---------- Judge.me JSON → review ---------- */
  const reviewFromJson = (raw, context) => {
    if (!raw || typeof raw !== 'object') return null;
    const rating = toNumber(raw.rating);
    const name = clean(raw.reviewer_name);
    const body = toParagraphs(raw.body_html || raw.body || '');
    const title = toParagraphs(raw.title || '').join(' ');
    const media = mediaOf(raw);
    if (rating == null || (!title && !body.length && !media.length && !name)) return null;
    const replyBody = toParagraphs(raw.reply_content || '');
    return {
      id: clean(raw.uuid) || `${name}|${clean(raw.created_at)}|${title}`,
      name,
      initials: initialsOf(name, raw.reviewer_initial, context.locale),
      verified: raw.verified_buyer === true || raw.verified_buyer === 'true',
      date: toDate(raw.created_at),
      rating: Math.max(0, Math.min(5, rating)),
      title,
      body,
      media,
      reply: replyBody.length ? { name: clean(raw.shop_reply_name) || context.shopName || '', body: replyBody } : null,
    };
  };

  const fromJson = (input, context = {}) => {
    let json = input;
    try {
      // A text metafield arrives as a JSON string inside JSON.
      for (let i = 0; i < 2 && typeof json === 'string'; i += 1) json = JSON.parse(json);
    } catch (error) {
      return null;
    }
    if (!json || typeof json !== 'object' || Array.isArray(json)) return null;
    if (json.number_of_reviews == null && !Array.isArray(json.reviews)) return null;

    const reviews = (Array.isArray(json.reviews) ? json.reviews : []).map((raw) => reviewFromJson(raw, context)).filter(Boolean);
    const histogram = (Array.isArray(json.histogram) ? json.histogram : [])
      .map((row) => ({ rating: toNumber(row && row.rating), count: toNumber(row && row.frequency) }))
      .filter((row) => row.rating != null && row.count != null);
    const gallery = (Array.isArray(json.photo_gallery) ? json.photo_gallery : []).map((raw) => reviewFromJson(raw, context)).filter((review) => review && review.media.length);
    const pagination = json.pagination && typeof json.pagination === 'object' ? json.pagination : {};
    const page = toNumber(pagination.current_page) || 1;
    const pages = toNumber(pagination.total_pages);

    return {
      source: 'json',
      count: toNumber(json.number_of_reviews),
      average: toNumber(json.average_rating),
      histogram: histogram.length ? histogram : null,
      reviews,
      gallery,
      page,
      hasMore: pages != null ? page < pages : null,
      sort: clean(json.sort_key),
    };
  };

  /* ---------- Pre-rendered Judge.me widget → the same JSON shape → review ----------
     The mapping mirrors the one in Judge.me's own widget/media.js. */
  const legacyReview = (el) => {
    const find = (selector) => el.querySelector(selector);
    const stamp = find('.jdgm-rev__timestamp');
    const rating = find('.jdgm-rev__rating');
    const body = find('.jdgm-rev__body');
    const reply = find('.jdgm-rev__reply-content');
    const title = find('.jdgm-rev__title');
    const pictures = [...el.querySelectorAll('.jdgm-rev__pic-link:not(.jdgm-rev__product-picture)')].map((link) => {
      const img = link.querySelector('.jdgm-rev__pic-img');
      return {
        compact: img ? img.getAttribute('data-src') || img.getAttribute('src') : '',
        huge: link.getAttribute('data-mfp-src') || link.getAttribute('href'),
        original: link.getAttribute('href'),
      };
    });
    return {
      uuid: el.getAttribute('data-review-id'),
      rating: rating ? rating.getAttribute('data-score') : null,
      created_at: stamp ? stamp.getAttribute('datetime') || stamp.getAttribute('data-content') : '',
      reviewer_name: clean(find('.jdgm-rev__author') && find('.jdgm-rev__author').textContent),
      reviewer_initial: clean(find('.jdgm-rev__icon') && find('.jdgm-rev__icon').textContent),
      verified_buyer: el.getAttribute('data-verified-buyer') === 'true' || !!find('.jdgm-rev__buyer-badge'),
      title: title ? title.textContent : '',
      body_node: body,
      reply_node: reply,
      shop_reply_name: clean(find('.jdgm-rev__replier') && find('.jdgm-rev__replier').textContent),
      pictures_urls: pictures,
      video_external_ids: [...el.querySelectorAll('.jdgm-rev__vids .jdgm-vid-player')].map((player) => player.getAttribute('data-external-id')).filter(Boolean),
      media_platform_hosted_video_infos: [...el.querySelectorAll('.jdgm-rev__vids .jdgm-yt-video')]
        .map((video) => ({ media_platform_name: 'yt', media_platform_url: video.getAttribute('data-id') }))
        .filter((video) => video.media_platform_url),
    };
  };

  const fromLegacy = (root, context = {}, extra = {}) => {
    if (!root || !root.querySelector) return null;
    const widget = root.matches && root.matches('.jdgm-rev-widg') ? root : root.querySelector('.jdgm-rev-widg');
    const scope = widget || root;
    const nodes = [...scope.querySelectorAll('.jdgm-rev')];
    if (!widget && !nodes.length && extra.total == null) return null;

    const reviews = nodes
      .map((node) => {
        const raw = legacyReview(node);
        const review = reviewFromJson({ ...raw, title: '', body_html: '', reply_content: '' }, context);
        if (!review) return null;
        review.title = clean(raw.title);
        review.body = toParagraphs(raw.body_node);
        const replyBody = toParagraphs(raw.reply_node);
        review.reply = replyBody.length ? { name: raw.shop_reply_name || context.shopName || '', body: replyBody } : null;
        return review;
      })
      .filter(Boolean);

    const histogram = [...scope.querySelectorAll('.jdgm-histogram__row[data-rating][data-frequency]')]
      .map((row) => ({ rating: toNumber(row.getAttribute('data-rating')), count: toNumber(row.getAttribute('data-frequency')) }))
      .filter((row) => row.rating != null && row.count != null);

    let gallery = [];
    const galleryData = scope.querySelector('.jdgm-gallery-data[data-json]');
    if (galleryData) {
      try {
        gallery = JSON.parse(galleryData.getAttribute('data-json'))
          .map((raw) => reviewFromJson(raw, context))
          .filter((review) => review && review.media.length);
      } catch (error) {
        gallery = [];
      }
    }

    const count = extra.total != null ? toNumber(extra.total) : widget ? toNumber(widget.getAttribute('data-number-of-reviews')) : null;
    const paginate = scope.querySelector('.jdgm-paginate');
    const next = paginate && paginate.querySelector('.jdgm-paginate__load-more, .jdgm-paginate__next-page');
    return {
      source: 'legacy',
      count,
      average: widget ? toNumber(widget.getAttribute('data-average-rating')) : null,
      histogram: histogram.length ? histogram : null,
      reviews,
      gallery,
      page: toNumber(extra.page) || 1,
      hasMore: next ? true : count != null ? null : false,
      sort: '',
    };
  };

  /* A result is usable when it has reviews to list, and "empty" when it says there are none. */
  const usable = (data) => !!data && data.reviews.length > 0;
  const knownEmpty = (data) => !!data && data.count === 0 && data.reviews.length === 0;

  class ProductReviews extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('script[data-reviews-config]');
      if (!configEl || this.ready) return;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (error) {
        return;
      }
      this.ready = true;
      this.strings = this.config.strings || {};
      this.context = { locale: this.config.locale || document.documentElement.lang || undefined, shopName: this.config.shopName || '' };
      this.productId = this.config.productId ? String(this.config.productId) : '';
      this.settled = false;
      this.reviews = [];
      this.ids = new Set();
      this.gallery = [];
      this.page = 1;

      const pick = (name) => this.querySelector(`[data-reviews-${name}]`);
      this.el = {
        filled: pick('filled'),
        empty: pick('empty'),
        average: pick('average'),
        stars: pick('stars'),
        count: pick('count'),
        bars: pick('bars'),
        total: pick('total'),
        sort: pick('sort'),
        media: pick('media'),
        tiles: pick('tiles'),
        list: pick('list'),
        more: pick('more'),
        moreButton: pick('more-button'),
        widget: pick('widget'),
      };
      this.templates = {
        review: this.querySelector('template[data-reviews-template="review"]'),
        tile: this.querySelector('template[data-reviews-template="tile"]'),
      };
      this.dialog = document.getElementById(this.config.dialog);
      // Start from a clean list when the element is connected again.
      if (this.el.list) this.el.list.replaceChildren();

      this.onClick = (event) => this.handleClick(event);
      this.addEventListener('click', this.onClick);
      this.setupLightbox();
      this.start();
    }

    disconnectedCallback() {
      this.stopWaiting();
      if (this.controller) this.controller.abort();
      this.removeEventListener('click', this.onClick);
      this.ready = false;
    }

    /* ---------- Reading ---------- */
    readEmbedded() {
      const script = this.querySelector('script[data-reviews-data]');
      return script ? fromJson(script.textContent, this.context) : null;
    }

    readPage() {
      const id = this.productId;
      const store = window.jdgm && window.jdgm.data && window.jdgm.data.reviewWidget;
      if (store && id && store[id]) {
        const data = fromJson(store[id], this.context);
        if (data) return data;
      }

      // The inline script of the Judge.me block, in case it could not run (window.jdgm missing).
      const scripts = [...this.querySelectorAll('script.jdgm-review-widget-data'), ...document.querySelectorAll('script.jdgm-review-widget-data')];
      for (const script of scripts) {
        const match = script.textContent.match(/reviewWidget\[\s*["']?(\d+)["']?\s*\]\s*=\s*(\{[\s\S]*\})\s*;?\s*$/);
        if (match && (!id || match[1] === id)) {
          const data = fromJson(match[2], this.context);
          if (data) return data;
        }
      }

      // Pre-rendered widget: inside this section first, then anywhere on the page.
      const widgets = [...this.querySelectorAll('.jdgm-rev-widg'), ...document.querySelectorAll('.jdgm-rev-widg')];
      const matching = widgets.filter((widget) => {
        // A widget that names another product (a second widget on the page) is skipped.
        const holder = widget.closest('.jdgm-review-widget, .jdgm-widget');
        const holderId = holder ? holder.getAttribute('data-product-id') || holder.getAttribute('data-id') : '';
        return !id || !holderId || holderId === id;
      });
      for (const widget of matching) {
        const data = fromLegacy(widget, this.context);
        if (data) return data;
      }
      return null;
    }

    async fetchPage(page) {
      if (!this.productId || !this.config.shop) return null;
      const host = httpsUrl(window.jdgm && window.jdgm.CDN_API_HOST) || API_HOST;
      const url = new URL(API_PATH, host.endsWith('/') ? host : `${host}/`);
      url.searchParams.set('product_id', this.productId);
      url.searchParams.set('shop_domain', this.config.shop);
      url.searchParams.set('platform', 'shopify');
      url.searchParams.set('page', String(page));

      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
      try {
        const response = await fetch(url.toString(), { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        const json = await response.json();
        if (json && typeof json.html === 'string') {
          const doc = new DOMParser().parseFromString(`<body>${json.html}</body>`, 'text/html');
          return fromLegacy(doc.body, this.context, { total: json.total_count, page: json.page || page });
        }
        const data = fromJson(json, this.context);
        if (data) data.page = page;
        return data;
      } catch (error) {
        return null;
      } finally {
        window.clearTimeout(timer);
      }
    }

    judgemePresent() {
      return !!(window.jdgm || window.jdgmSettings || document.querySelector('.jdgm-widget, script.jdgm-review-widget-data'));
    }

    /* ---------- Flow ---------- */
    start() {
      const first = this.readEmbedded();
      const data = usable(first) || knownEmpty(first) ? first : this.readPage() || first;
      if (this.resolve(data)) return;

      // Nothing readable yet: ask the endpoint and keep an eye on the page for a late widget.
      let request = Promise.resolve(null);
      if (this.productId && ((this.config.count || 0) > 0 || this.judgemePresent())) {
        request = this.fetchPage(1).then((result) => {
          if (!this.settled) this.resolve(result);
          return result;
        });
      }

      if ('MutationObserver' in window) {
        let queued = 0;
        this.observer = new MutationObserver(() => {
          window.clearTimeout(queued);
          queued = window.setTimeout(() => {
            if (!this.settled) this.resolve(this.readPage());
          }, 120);
        });
        this.observer.observe(document.body, { childList: true, subtree: true });
      }

      this.waitTimer = window.setTimeout(async () => {
        await request;
        if (this.settled) return;
        if (!this.resolve(this.readPage())) this.fallback();
      }, WAIT_FOR_WIDGET);
    }

    stopWaiting() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      window.clearTimeout(this.waitTimer);
    }

    /* Shows a result when it is conclusive. Returns false when the search should go on. */
    resolve(data) {
      if (this.settled) return true;
      if (usable(data)) {
        this.settled = true;
        this.stopWaiting();
        this.show(data);
        return true;
      }
      if (knownEmpty(data)) {
        this.settled = true;
        this.stopWaiting();
        this.setState('empty');
        return true;
      }
      return false;
    }

    /* Judge.me could not be read: show its own widget when there is one in this section. */
    fallback() {
      this.settled = true;
      this.stopWaiting();
      const holder = this.el.widget;
      if (holder && holder.querySelector(WIDGET_ROOTS)) {
        this.setState('widget');
        return;
      }
      // No widget either: keep what the section rendered (summary or empty state).
      if (this.el.bars) this.el.bars.hidden = true;
    }

    setState(state) {
      this.dataset.state = state;
      const filled = state !== 'empty';
      if (this.el.filled) this.el.filled.hidden = !filled;
      if (this.el.empty) this.el.empty.hidden = filled;
      const raw = state === 'widget';
      if (this.el.widget) this.el.widget.hidden = !raw;
      if (raw) {
        [this.el.list, this.el.media, this.el.more, this.el.bars, this.el.sort].forEach((el) => {
          if (el) el.hidden = true;
        });
      }
    }

    show(data) {
      this.setState('filled');
      this.page = data.page || 1;
      this.total = data.count != null && data.count > 0 ? data.count : Math.max(this.config.count || 0, data.reviews.length);
      this.renderSummary(data);
      this.renderBars(data.histogram);
      this.renderSort(data.sort);
      this.galleryFromData = data.gallery && data.gallery.length > 0;
      if (this.galleryFromData) this.gallery = this.flatten(data.gallery);
      this.append(data.reviews);
      this.updateMore(data, data.reviews.length);
    }

    /* ---------- Rendering ---------- */
    format(template, values) {
      return String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
    }

    number(value, digits) {
      try {
        const options = digits == null ? {} : { minimumFractionDigits: digits, maximumFractionDigits: digits };
        return new Intl.NumberFormat(this.context.locale, options).format(value);
      } catch (error) {
        return digits == null ? String(value) : value.toFixed(digits);
      }
    }

    plural(count, one, other) {
      return this.format(count === 1 ? one : other, { number: this.number(count) });
    }

    setStars(el, rating) {
      if (!el) return;
      const value = Math.max(0, Math.min(5, rating || 0));
      el.style.setProperty('--fill', `${Math.round(value * 200) / 10}%`);
      const shown = Number.isInteger(value) ? this.number(value) : this.number(value, 1);
      el.setAttribute('aria-label', this.format(this.strings.ratingLabel, { rating: shown }));
    }

    renderSummary(data) {
      const average = data.average != null && data.average > 0 ? data.average : null;
      if (average != null) {
        if (this.el.average) this.el.average.textContent = this.number(average, 1);
        if (this.el.stars) {
          this.setStars(this.el.stars, Math.round(average * 10) / 10);
          this.el.stars.setAttribute('aria-label', this.format(this.strings.ratingLabel, { rating: this.number(average, 1) }));
        }
      }
      if (this.el.count) this.el.count.textContent = this.plural(this.total, this.strings.reviewsOne, this.strings.reviewsOther);
      if (this.el.total) this.el.total.textContent = this.plural(this.total, this.strings.commentsOne, this.strings.commentsOther);
    }

    renderBars(histogram) {
      const bars = this.el.bars;
      if (!bars) return;
      if (!histogram) {
        bars.hidden = true;
        return;
      }
      const sum = histogram.reduce((total, row) => total + row.count, 0);
      bars.querySelectorAll('[data-reviews-bar]').forEach((row) => {
        const stars = Number(row.getAttribute('data-reviews-bar'));
        const entry = histogram.find((item) => item.rating === stars);
        const count = entry ? entry.count : 0;
        const share = sum > 0 ? Math.round((count / sum) * 1000) / 10 : 0;
        row.querySelector('[data-reviews-bar-fill]').style.setProperty('--value', `${share}%`);
        row.querySelector('[data-reviews-bar-count]').textContent = this.number(count);
        row.querySelector('[data-reviews-bar-label]').textContent = this.format(this.strings.barLabel, { stars, number: this.number(count) });
      });
      bars.classList.remove('is-pending');
      bars.hidden = false;
    }

    renderSort(sort) {
      if (!this.el.sort) return;
      const key = clean(sort).toLowerCase().replace(/-/g, '_');
      const labels = {
        created_at: this.strings.sortNewest,
        most_recent: this.strings.sortNewest,
        highest_rating: this.strings.sortHighest,
        lowest_rating: this.strings.sortLowest,
        most_helpful: this.strings.sortHelpful,
        with_pictures: this.strings.sortMedia,
        pictures_first: this.strings.sortMedia,
        videos_first: this.strings.sortMedia,
        with_media: this.strings.sortMedia,
      };
      const label = labels[key] || '';
      this.el.sort.textContent = label;
      this.el.sort.hidden = !label;
    }

    dateText(date) {
      try {
        return new Intl.DateTimeFormat(this.context.locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
      } catch (error) {
        return date.toLocaleDateString();
      }
    }

    fillBody(target, paragraphs) {
      paragraphs.forEach((paragraph) => {
        const p = document.createElement('p');
        paragraph.split('\n').forEach((line, index) => {
          if (index) p.appendChild(document.createElement('br'));
          p.appendChild(document.createTextNode(line));
        });
        target.appendChild(p);
      });
    }

    buildTile(item, name, label) {
      const node = this.templates.tile.content.firstElementChild.cloneNode(true);
      const button = node.querySelector('[data-reviews-open]');
      const img = node.querySelector('img');
      const isVideo = item.type === 'video';
      // The name is hidden text, not aria-label, so it never disagrees with the visible "+3" chip.
      node.querySelector('[data-tile-label]').textContent = label || this.format(isVideo ? this.strings.openVideo : this.strings.openPhoto, { name });
      if (item.thumb) {
        img.src = item.thumb;
        img.addEventListener('error', () => img.remove(), { once: true });
      } else {
        img.remove();
      }
      if (!isVideo) node.querySelector('[data-tile-video]').remove();
      node.querySelector('[data-tile-more]').remove();
      return node;
    }

    buildReview(review) {
      const node = this.templates.review.content.firstElementChild.cloneNode(true);
      const part = (name) => node.querySelector(`[data-review-${name}]`);
      const name = review.name || this.strings.anonymous || '';

      part('initials').textContent = review.initials || initialsOf(name, '', this.context.locale);
      part('name').textContent = name;
      if (!review.verified) part('verified').remove();

      const time = part('date');
      if (review.date) {
        time.dateTime = review.date.toISOString();
        time.textContent = this.dateText(review.date);
      } else {
        time.remove();
      }

      this.setStars(part('stars'), review.rating);

      const title = part('title');
      if (review.title) title.textContent = review.title;
      else title.remove();

      const body = part('body');
      if (review.body.length) this.fillBody(body, review.body);
      else body.remove();

      const media = part('media');
      if (review.media.length) {
        const items = review.media.map((item) => ({ ...item, name, rating: review.rating, title: review.title }));
        items.forEach((item, index) => {
          const tile = this.buildTile(item, name);
          const button = tile.querySelector('[data-reviews-open]');
          button._lightbox = { items, index };
          media.appendChild(tile);
        });
      } else {
        media.remove();
      }

      const reply = part('reply');
      if (review.reply) {
        const by = part('reply-by');
        if (review.reply.name) by.textContent = this.format(this.strings.replyBy, { name: review.reply.name });
        else by.remove();
        this.fillBody(part('reply-body'), review.reply.body);
      } else {
        reply.remove();
      }
      return node;
    }

    flatten(reviews) {
      const items = [];
      const seen = new Set();
      reviews.forEach((review) => {
        const name = review.name || this.strings.anonymous || '';
        review.media.forEach((item) => {
          if (seen.has(item.src)) return;
          seen.add(item.src);
          items.push({ ...item, name, rating: review.rating, title: review.title });
        });
      });
      return items;
    }

    /* Adds reviews that are not listed yet. Returns the nodes that were added. */
    append(reviews) {
      const added = [];
      const fragment = document.createDocumentFragment();
      reviews.forEach((review) => {
        if (this.ids.has(review.id)) return;
        this.ids.add(review.id);
        this.reviews.push(review);
        const node = this.buildReview(review);
        fragment.appendChild(node);
        added.push(node);
      });
      if (this.el.list) this.el.list.appendChild(fragment);
      // Without a gallery from Judge.me the strip shows the media of the listed reviews.
      if (!this.galleryFromData) this.gallery = this.flatten(this.reviews);
      if (!this.galleryFromData || !this.tilesReady) this.renderTiles();
      this.tilesReady = true;
      return added;
    }

    renderTiles() {
      const { media, tiles } = this.el;
      if (!media || !tiles) return;
      const items = this.gallery;
      tiles.replaceChildren();
      items.slice(0, TILE_LIMIT).forEach((item, index) => {
        const rest = items.length - TILE_LIMIT;
        const last = index === TILE_LIMIT - 1 && rest > 0;
        const tile = this.buildTile(item, item.name, last ? this.format(this.strings.openAll, { number: this.number(items.length) }) : '');
        const button = tile.querySelector('[data-reviews-open]');
        button._lightbox = { items, index };
        if (last) {
          const more = this.templates.tile.content.querySelector('[data-tile-more]').cloneNode(true);
          more.textContent = `+${this.number(rest)}`;
          button.appendChild(more);
        }
        tiles.appendChild(tile);
      });
      media.hidden = items.length === 0;
    }

    updateMore(data, added) {
      let more = data.hasMore;
      if (more == null) more = this.reviews.length < this.total;
      if (!added) more = false;
      this.hasMore = !!more && !!this.productId && !!this.config.shop;
      if (this.el.more) this.el.more.hidden = !this.hasMore;
    }

    async loadMore() {
      const button = this.el.moreButton;
      if (!button || this.loading || !this.hasMore) return;
      this.loading = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      const data = await this.fetchPage(this.page + 1);
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      this.loading = false;
      if (!this.isConnected) return;

      if (!usable(data)) {
        // Nothing more to read: stop offering it.
        this.hasMore = false;
        if (this.el.more) this.el.more.hidden = true;
        return;
      }
      this.page += 1;
      const added = this.append(data.reviews);
      this.updateMore(data, added.length);
      if (added.length) {
        const message = this.plural(added.length, this.strings.loaded, this.strings.loaded);
        if (window.LM && window.LM.announce) window.LM.announce(message);
        if (!this.hasMore) {
          // The button is gone: continue reading from the first new review.
          added[0].setAttribute('tabindex', '-1');
          added[0].focus({ preventScroll: true });
        }
      }
    }

    handleClick(event) {
      const opener = event.target.closest('[data-reviews-open]');
      if (opener && opener._lightbox) {
        event.preventDefault();
        this.openLightbox(opener._lightbox.items, opener._lightbox.index, opener);
        return;
      }
      if (event.target.closest('[data-reviews-more-button]')) {
        event.preventDefault();
        this.loadMore();
      }
    }

    /* ---------- Lightbox ---------- */
    setupLightbox() {
      const dialog = this.dialog;
      if (!dialog || dialog._reviewsReady) return;
      dialog._reviewsReady = true;
      this.light = {
        stage: dialog.querySelector('[data-lightbox-stage]'),
        caption: dialog.querySelector('[data-lightbox-caption]'),
        counter: dialog.querySelector('[data-lightbox-counter]'),
        nav: dialog.querySelector('[data-lightbox-nav]'),
        prev: dialog.querySelector('[data-lightbox-prev]'),
        next: dialog.querySelector('[data-lightbox-next]'),
        items: [],
        index: 0,
      };
      this.light.prev.addEventListener('click', () => this.stepLightbox(-1));
      this.light.next.addEventListener('click', () => this.stepLightbox(1));
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.stepLightbox(-1);
        if (event.key === 'ArrowRight') this.stepLightbox(1);
      });
      // Emptying the stage stops a playing video.
      dialog.addEventListener('close', () => this.light.stage.replaceChildren());
    }

    openLightbox(items, index, opener) {
      if (!this.dialog || !this.light || !items.length) return;
      this.light.items = items;
      this.light.index = index;
      this.renderLightbox();
      if (window.LM && window.LM.openDialog) window.LM.openDialog(this.dialog, opener);
      else if (!this.dialog.open) this.dialog.showModal();
    }

    stepLightbox(direction) {
      const { items } = this.light;
      if (items.length < 2) return;
      this.light.index = (this.light.index + direction + items.length) % items.length;
      this.renderLightbox();
    }

    renderLightbox() {
      const { items, index, stage, caption, counter, nav } = this.light;
      const item = items[index];
      let media;
      if (item.type === 'video') {
        media = document.createElement('iframe');
        media.src = item.src;
        media.title = this.format(this.strings.videoTitle, { name: item.name });
        media.allow = 'fullscreen; picture-in-picture';
        media.allowFullscreen = true;
        media.loading = 'lazy';
        media.referrerPolicy = 'strict-origin-when-cross-origin';
      } else {
        media = document.createElement('img');
        media.src = item.src;
        media.alt = this.format(this.strings.photoAlt, { name: item.name });
        media.decoding = 'async';
      }
      stage.replaceChildren(media);

      caption.replaceChildren();
      const name = document.createElement('strong');
      name.textContent = item.name;
      caption.appendChild(name);
      if (item.title) {
        const title = document.createElement('span');
        title.textContent = item.title;
        caption.appendChild(title);
      }
      counter.textContent = this.format(this.strings.counter, { current: this.number(index + 1), total: this.number(items.length) });
      nav.hidden = items.length < 2;
    }
  }

  // Exposed for tests: the two readers are pure functions.
  ProductReviews.fromJson = fromJson;
  ProductReviews.fromLegacy = fromLegacy;

  customElements.define('product-reviews', ProductReviews);
})();
